/**
 * `IMergeModel` — port of Unity `Models/MergeModel.cs` +
 * `SelectedGameObjectModel.MergeParts` / `UnmergeParts`
 * (`SelectedGameObjectModel.cs:1805-1841`).
 *
 * Unity merges by mesh surgery: `RuntimeObjectProperties.MergeChildren`
 * combines the subtree's renderers into the merged node, and
 * `MergeModel.DestroyMergedRuntimeObjects` strips the descendants of the
 * `CampfireExposeToEditor` component that made them addressable. The
 * `Merged` snapshot flag is only the persisted record of that.
 *
 * The web port has no mesh surgery — the renderer keeps one glTF node per
 * snapshot either way — so here the flag IS the feature. Everything that
 * reads it (hierarchy rows, picking, visibility, AI scene listing) was wired
 * up when the flag started loading; this model is the write side, and it is
 * the only place allowed to write it.
 */

import type { Emitter } from "@core/events/emitter";

export interface MergeChangedEvent {
  readonly sceneId: string;
  /** Nodes whose own flag flipped (not the descendants cleared by un-merge). */
  readonly snapshotIds: ReadonlyArray<string>;
  readonly merged: boolean;
}

export interface IMergeModel {
  readonly onMergeChanged: Emitter<MergeChangedEvent>;

  /**
   * Unity `EntityContextView.MergePartsIsVisible`/`CanMergeParts`: at least
   * one target still has unmerged children, and no descendant of a target
   * belongs to a selection set (merging would swallow a set member).
   *
   * Unity splits those into "visible" and "enabled" so the button greys out
   * rather than disappearing when a set is in the way; {@link mergeBlockedBy}
   * is what lets a caller reproduce that.
   */
  canMerge(sceneId: string, snapshotIds: ReadonlyArray<string>): boolean;

  /** `"selection-set"` when merge is otherwise possible but a set blocks it. */
  mergeBlockedBy(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
  ): "selection-set" | null;

  /** Unity `CanUnmergeParts`: no target can merge, and at least one is merged. */
  canUnmerge(sceneId: string, snapshotIds: ReadonlyArray<string>): boolean;

  /**
   * Set `HasMergedChildren` on each resolved target. Returns the snapshot ids
   * that actually changed.
   */
  mergeAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<string>>;

  /**
   * Clear `HasMergedChildren` on each resolved target AND on every descendant
   * that still carries it — Unity's un-merge reverts nested merges in one go
   * (`ProjectModel_Entities.cs:322-334`).
   */
  unmergeAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<string>>;
}

export const MERGE_MODEL = Symbol.for("Campfire.MergeModel");
